import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Container, Button, IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, useTheme, Menu, MenuItem } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AppBar from '@material-ui/core/AppBar';
import logo from 'assets/img/cybros-logo-i.png';
import useHeaderStyles from '../assets/css/_header';

export default function Header(props) {
  const classes = useHeaderStyles();
  const { sections, title } = props;
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (e, menuId) => {
    setAnchorEl(e.currentTarget);
    setOpenMenuId(menuId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenMenuId(null);
  };

  // Scroll to a section by ID
  const scrollToSection = useCallback((targetId) => {
    const element = document.getElementById(targetId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  // Handle logo click - navigate to home
  const handleLogoClick = () => {
    // Check if already on home page
    const isHomePage = window.location.pathname === '/' || window.location.pathname === '';
    if (!isHomePage) {
      // Navigate to home
      window.location.href = '/';
      return;
    }
    // On home page, scroll to top
    scrollToSection('home');
    window.history.replaceState({ section: 'home' }, '', '/');
    setActiveSection('home');
  };

  // Handle smooth scroll navigation
  const handleNavClick = (e, url) => {
    e.preventDefault();
    
    // Check if it's an external page link (starts with /)
    if (url.startsWith('/')) {
      // For external page navigation, use location.href for full page transition
      window.location.href = url;
      return;
    }
    
    const targetId = url.replace('#', '');
    const currentPath = window.location.pathname;
    const isHomePage = currentPath === '/' || currentPath === '';
    
    // Check if target section exists on current page
    const targetElement = document.getElementById(targetId);
    
    // If on different page and target doesn't exist, navigate to home first
    if (!isHomePage && !targetElement) {
      // Navigate to home page with section hash
      window.location.href = `/#${targetId}`;
      return;
    }
    
    // If we're on home page and target doesn't exist, just scroll to home
    if (!targetElement) {
      scrollToSection('home');
      window.history.pushState({ section: 'home' }, '', '/');
      setActiveSection('home');
      handleMenuClose();
      return;
    }
    
    scrollToSection(targetId);

    // Update URL cleanly - show clean path for all sections
    const cleanPath = targetId === 'home' ? '/' : `/${targetId}`;
    window.history.pushState({ section: targetId }, '', cleanPath);
    setActiveSection(targetId);
    
    // Close mobile drawer after navigation
    if (mobileOpen) {
      setMobileOpen(false);
    }
    
    // Close menu after navigation
    handleMenuClose();
  };

  // Handle initial page load - scroll to section if URL has a path
  useEffect(() => {
    const path = window.location.pathname.replace('/', '');
    if (path && path !== '') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        scrollToSection(path);
        setActiveSection(path);
      }, 100);
    }
  }, [scrollToSection]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.replace('/', '');
      const targetSection = path || 'home';
      scrollToSection(targetSection);
      setActiveSection(targetSection);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [scrollToSection]);

  // Track scroll position for active section and header style
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Header background change on scroll
          setScrolled(window.scrollY > 50);

          // Only update section state based on scroll if we're on home page
          const currentPath = window.location.pathname;
          const isHomePage = currentPath === '/' || currentPath === '';
          
          if (isHomePage) {
            // Determine active section based on scroll position
            const sectionIds = sections.map(s => s.url.replace('#', ''));
            let newActiveSection = 'home';
            
            for (let i = sectionIds.length - 1; i >= 0; i--) {
              const element = document.getElementById(sectionIds[i]);
              if (element) {
                const rect = element.getBoundingClientRect();
                if (rect.top <= 150) {
                  newActiveSection = sectionIds[i];
                  break;
                }
              }
            }
            
            // Update URL and active state based on scroll position
            if (newActiveSection !== activeSection) {
              const cleanPath = newActiveSection === 'home' ? '/' : `/${newActiveSection}`;
              window.history.replaceState({ section: newActiveSection }, '', cleanPath);
              setActiveSection(newActiveSection);
            }
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections, activeSection]);

  return (
    <AppBar 
      position="fixed" 
      className={`${classes.header} ${scrolled ? classes.headerScrolled : ''}`}
    >
      <Container fixed>
      <div className={classes.headerContainer}>
        {/* Logo */}
        <div className={classes.logoSection} onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <img src={logo} alt={title} className={classes.logo} />
        </div>

        {/* Desktop Navigation */}
        {!isMobile && (
          <nav className={classes.navMenu}>
            {sections.map((section) => {
              const sectionId = section.url.replace('#', '');
              const isActive = activeSection === sectionId;
              
              if (section.type === 'btn') {
                return (
                  <Button
                    key={section.title}
                    variant="contained"
                    onClick={(e) => handleNavClick(e, section.url)}
                    className={`${classes.toolbarButton} ${isActive ? classes.activeButton : ''}`}
                  >
                    {section.title}
                  </Button>
                );
              } else if (section.type === 'dropdown' && section.submenu) {
                return (
                  <div key={section.title} className={classes.dropdownContainer}>
                    <div
                      onClick={(e) => handleMenuOpen(e, section.title)}
                      className={`${classes.toolbarLink} ${isActive ? classes.activeLink : ''}`}
                    >
                      {section.title}
                      <ExpandMoreIcon className={classes.expandIcon} />
                    </div>
                    <Menu
                      anchorEl={anchorEl}
                      open={openMenuId === section.title}
                      onClose={handleMenuClose}
                      className={classes.dropdown}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    >
                      {section.submenu.map((submenu) => (
                        <MenuItem
                          key={submenu.title}
                          onClick={(e) => handleNavClick(e, submenu.url)}
                          className={classes.dropdownItem}
                        >
                          {submenu.title}
                        </MenuItem>
                      ))}
                    </Menu>
                  </div>
                );
              } else {
                return (
                  <a
                    key={section.title}
                    href={section.url}
                    onClick={(e) => handleNavClick(e, section.url)}
                    className={`${classes.toolbarLink} ${isActive ? classes.activeLink : ''}`}
                  >
                    {section.title}
                  </a>
                );
              }
            })}
          </nav>
        )}

        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            edge="end"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
            aria-label="open drawer"
          >
            <MenuIcon className={classes.menuIcon} />
          </IconButton>
        )}
      </div>
      </Container>
      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        classes={{ paper: classes.drawerPaper }}
      >
        <div className={classes.drawerHeader}>
          <img src={logo} alt={title} className={classes.drawerLogo} />
          <IconButton onClick={handleDrawerToggle} className={classes.closeButton}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </div>
        <List className={classes.drawerList}>
          {sections.map((section) => {
            const sectionId = section.url.replace('#', '');
            const isActive = activeSection === sectionId;
            
            if (section.type === 'dropdown' && section.submenu) {
              return (
                <div key={section.title}>
                  <ListItem
                    button
                    className={`${classes.drawerItem} ${isActive ? classes.drawerItemActive : ''}`}
                  >
                    <ListItemText 
                      primary={section.title} 
                      classes={{ primary: classes.drawerItemText }}
                    />
                  </ListItem>
                  {section.submenu.map((submenu) => (
                    <ListItem
                      button
                      key={submenu.title}
                      onClick={(e) => handleNavClick(e, submenu.url)}
                      className={classes.drawerSubmenuItem}
                    >
                      <ListItemText 
                        primary={submenu.title} 
                        classes={{ primary: classes.drawerSubmenuText }}
                      />
                    </ListItem>
                  ))}
                </div>
              );
            }
            
            return (
              <ListItem
                button
                key={section.title}
                onClick={(e) => handleNavClick(e, section.url)}
                className={`${classes.drawerItem} ${isActive ? classes.drawerItemActive : ''}`}
              >
                <ListItemText 
                  primary={section.title} 
                  classes={{ primary: classes.drawerItemText }}
                />
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </AppBar>
  );
}

Header.propTypes = {
  sections: PropTypes.array,
  title: PropTypes.string,
};
